import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { haptic, HAPTIC_START, HAPTIC_STOP } from "@/lib/langs";

const MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
const SILENCE_MS = 900;
const MIN_VOICE_MS = 350;
const MAX_MS = 30000;
const MIC = { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } };

const openMeter = (stream) => {
  const ac = new (window.AudioContext || window.webkitAudioContext)();
  const an = ac.createAnalyser();
  an.fftSize = 1024;
  ac.createMediaStreamSource(stream).connect(an);
  const buf = new Float32Array(an.fftSize);
  const rms = () => {
    an.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    return Math.sqrt(sum / buf.length);
  };
  return { ac, rms };
};

const startRecorder = (stream, onBlob) => {
  const mime = MIMES.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
  const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const chunks = [];
  r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
  r.onstop = () => onBlob(new Blob(chunks, { type: (r.mimeType || "audio/webm").split(";")[0] }));
  r.start();
  haptic(HAPTIC_START);
  return r;
};

// Voice activity detection loop: opens a recorder on speech, closes it after silence.
const runDetector = (stream, s) => {
  const { ac, rms } = openMeter(stream);
  s.ac = ac;
  let frame = 0;
  const loop = () => {
    s.raf = requestAnimationFrame(loop);
    const level = rms();
    const now = performance.now();
    if (++frame % 3 === 0) s.setLevel(Math.min(1, level * 12));
    const loud = level > s.refs.threshold.current;
    if (loud) s.lastVoice = now;
    const paused = s.refs.paused.current;
    if (!s.recorder && loud && !paused) s.begin(now);
    else if (s.recorder && (now - s.lastVoice > SILENCE_MS || now - s.startedAt > MAX_MS)) s.stopRec();
    else if (!s.recorder) s.setState(paused ? "paused" : "listening");
  };
  loop();
};

const createSession = ({ refs, cb, setState, setLevel }) => {
  const s = { refs, setState, setLevel, recorder: null, stream: null, ac: null, raf: 0, startedAt: 0, lastVoice: 0, stopped: false };
  s.stopRec = () => {
    const r = s.recorder;
    s.recorder = null;
    if (r?.state === "recording") r.stop();
  };
  s.begin = (now) => {
    const begin = now;
    s.recorder = startRecorder(s.stream, (blob) => {
      if (s.stopped) return;
      const voiced = s.lastVoice - begin >= MIN_VOICE_MS;
      if (voiced) haptic(HAPTIC_STOP);
      setState(refs.paused.current ? "paused" : "listening");
      if (voiced) cb.current.onUtterance(blob);
    });
    s.startedAt = now;
    setState("capturing");
  };
  s.start = async () => {
    try {
      s.stream = await navigator.mediaDevices.getUserMedia(MIC);
    } catch {
      toast.error("Microphone unavailable or permission denied");
      cb.current.onFail?.();
      return;
    }
    if (s.stopped) s.stream.getTracks().forEach((t) => t.stop());
    else runDetector(s.stream, s);
  };
  s.dispose = () => {
    s.stopped = true;
    cancelAnimationFrame(s.raf);
    s.stopRec();
    s.stream?.getTracks().forEach((t) => t.stop());
    s.ac?.close().catch(() => {});
  };
  return s;
};

// Hands-free voice activity detection: records each utterance automatically.
export function useHandsFree({ enabled, paused, threshold, onUtterance, onFail }) {
  const [state, setState] = useState("off");
  const [level, setLevel] = useState(0);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const thresholdRef = useRef(threshold);
  thresholdRef.current = threshold;
  const cb = useRef({ onUtterance, onFail });
  cb.current = { onUtterance, onFail };

  useEffect(() => {
    if (!enabled) return undefined;
    setState("listening");
    const session = createSession({ refs: { paused: pausedRef, threshold: thresholdRef }, cb, setState, setLevel });
    session.start();
    return () => {
      session.dispose();
      setState("off");
      setLevel(0);
    };
  }, [enabled]);

  return { state, level, recording: state === "capturing" };
}
